import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MapPin,
  Calendar,
  Star,
  Users,
  Trophy,
  Search,
  Filter,
  ExternalLink,
  Map as MapIcon
} from 'lucide-react';
import { CheckInService } from '../services/checkInService';
import { CheckIn, Achievement, CheckInFilters } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface CheckInsTabProps {
  userId?: string;
  isOwner?: boolean;
  isPublicView?: boolean;
}

export const CheckInsTab: React.FC<CheckInsTabProps> = ({
  userId,
  isOwner = false,
  isPublicView = false
}) => {
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'recent' | 'rated'>('all');
  const [totalCount, setTotalCount] = useState(0);
  const [uniqueDisplays, setUniqueDisplays] = useState(0);

  useEffect(() => {
    loadCheckIns();
  }, [userId, filter, searchTerm]);

  const loadCheckIns = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const filters: CheckInFilters = {
        is_public: isPublicView ? true : undefined,
      };

      if (filter === 'recent') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        filters.date_range = {
          start: thirtyDaysAgo.toISOString(),
          end: new Date().toISOString(),
        };
      } else if (filter === 'rated') {
        filters.has_rating = true;
      }

      const history = await CheckInService.getUserCheckInHistory(userId, filters, 50, 0);

      let filteredCheckins = history.checkins;
      if (searchTerm) {
        filteredCheckins = filteredCheckins.filter(checkin =>
          checkin.displays?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          checkin.note?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setCheckins(filteredCheckins);
      setAchievements(history.achievements);
      setTotalCount(history.total_count);
      setUniqueDisplays(history.unique_displays);
    } catch (error) {
      console.error('Error loading check-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const achievementIcons: Record<string, string> = {
    first_checkin: '🎉',
    explorer_10: '🔟',
    explorer_25: '🏆',
    explorer_50: '⭐',
    location_verified: '📍',
    weekend_warrior: '🌟',
    early_bird: '🌅',
    night_owl: '🦉',
  };

  const achievementNames: Record<string, string> = {
    first_checkin: 'First Check-in',
    explorer_10: 'Explorer (10 visits)',
    explorer_25: 'Adventurer (25 visits)',
    explorer_50: 'Master Explorer (50 visits)',
    location_verified: 'Location Verified',
    weekend_warrior: 'Weekend Warrior',
    early_bird: 'Early Bird',
    night_owl: 'Night Owl',
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-lg"></div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-2xl font-bold">{totalCount}</p>
                <p className="text-xs text-muted-foreground">
                  Total Check-ins
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <MapIcon className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-2xl font-bold">{uniqueDisplays}</p>
                <p className="text-xs text-muted-foreground">
                  Unique Displays
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Trophy className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-2xl font-bold">{achievements.length}</p>
                <p className="text-xs text-muted-foreground">
                  Achievements
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Achievements */}
      {achievements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Achievements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {achievements.map((achievement) => (
                <Badge
                  key={achievement.id}
                  variant="secondary"
                  className="bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800"
                >
                  <span className="mr-1">
                    {achievementIcons[achievement.achievement_type] || '🏅'}
                  </span>
                  {achievementNames[achievement.achievement_type] || achievement.achievement_type}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search displays or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'recent', 'rated'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f as typeof filter)}
            >
              {f === 'all' && 'All'}
              {f === 'recent' && 'Recent'}
              {f === 'rated' && 'Rated'}
            </Button>
          ))}
        </div>
      </div>

      {/* Check-ins List */}
      <div className="space-y-4">
        {checkins.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="font-medium mb-2">
                {isPublicView ? 'No public check-ins' : 'No check-ins yet'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isOwner && !isPublicView
                  ? 'Start exploring displays and check in to your first one!'
                  : 'This user hasn\'t shared any public check-ins yet.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          checkins.map((checkin) => (
            <Card key={checkin.id}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <Link
                          to={`/displays/${checkin.display_id}`}
                          className="font-medium text-gray-900 hover:text-blue-600 flex items-center gap-1"
                        >
                          {checkin.displays?.name || 'Unknown Display'}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                        <p className="text-sm text-muted-foreground mt-1">
                          {checkin.displays?.address}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {checkin.location_verified && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                            <MapPin className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(checkin.checked_in_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>

                    {checkin.rating && (
                      <div className="flex items-center gap-1 mt-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={cn(
                              'w-4 h-4',
                              i < checkin.rating!
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            )}
                          />
                        ))}
                      </div>
                    )}

                    {checkin.note && (
                      <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-3 rounded-lg">
                        "{checkin.note}"
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                      <span>
                        {format(new Date(checkin.checked_in_at), 'MMM d, yyyy • h:mm a')}
                      </span>
                      {!checkin.is_public && (
                        <Badge variant="secondary" className="text-xs">
                          Private
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};